import { NotificationCampaign } from '@/redux/models';

// The render-time tag allowlist lives in renderMessageMarkup.tsx (ALLOWED_TAGS), which
// is the only place that consumes it. A second copy used to sit here, inert — a
// security allowlist that looked authoritative, was read by nothing, and could drift
// from the one that actually gates rendering.

export type NotificationLink =
  | { kind: 'internal'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'rejected' };

// A campaign's ctaLink is FREE TEXT typed into the admin compose form; a personal
// notification's link is composed in code. Either can be absolute, and react-router's
// navigate() treats an absolute URL as a RELATIVE path — navigate('http://amlbot.loc/x')
// lands on '/http://amlbot.loc/x' and 404s. Classify first, then let the caller pick
// the right primitive.
//
// http and https are treated identically; nothing is upgraded. Every OTHER scheme is
// rejected outright, because an operator-typed 'javascript:...' handed to
// window.location would execute against a logged-in customer's session.
export const resolveNotificationLink = (
  link: string | null | undefined,
): NotificationLink => {
  const href = (link || '').trim();
  if (!href) return { kind: 'rejected' };

  // Protocol-relative ('//host/path') is absolute; the browser supplies the scheme.
  if (href.startsWith('//')) return { kind: 'external', href };

  // Root-relative is the normal case for our own routes.
  if (href.startsWith('/')) return { kind: 'internal', href };

  const scheme = href.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase();
  if (!scheme) return { kind: 'internal', href: `/${href}` };

  return scheme === 'http' || scheme === 'https'
    ? { kind: 'external', href }
    : { kind: 'rejected' };
};

// Shared by the bell dropdown and the All Notifications page so a link can never
// behave differently depending on where it was clicked.
export const navigateToNotificationLink = (
  link: string | null | undefined,
  navigate: (to: string) => void,
): void => {
  const resolved = resolveNotificationLink(link);

  if (resolved.kind === 'internal') {
    navigate(resolved.href);
  } else if (resolved.kind === 'external') {
    window.location.assign(resolved.href);
  }
  // 'rejected' deliberately does nothing: an unsupported scheme is not a reason to
  // navigate somewhere arbitrary, and silently staying put is safer than guessing.
};

// Campaigns carry EN/UA/RU only. Any other UI locale (cn) falls back to English,
// as does an empty translation — an operator may leave a locale blank.
export const selectCampaignCopy = (
  campaign: NotificationCampaign,
  locale: string,
): { title: string; body: string } => {
  const key = locale.toLowerCase().slice(0, 2);

  const byLocale: Record<string, { title: string; body: string }> = {
    ua: { title: campaign.titleUa, body: campaign.bodyUa },
    ru: { title: campaign.titleRu, body: campaign.bodyRu },
  };

  const picked = byLocale[key];

  return {
    title: picked?.title || campaign.titleEn,
    body: picked?.body || campaign.bodyEn,
  };
};
